// ROOM 404: Main Application Root Shell
// Secure Perimeter, Relaxed Interior

import React, { useEffect, useState } from 'react';
import { Header } from './components/Header';
import { InstallGuideModal } from './components/InstallGuideModal';
import { Navigation, type NavTab } from './components/Navigation';
import { NotificationsModal } from './components/NotificationsModal';
import { PushConsentModal } from './components/PushConsentModal';
import { ReportModal } from './components/ReportModal';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminDashboard } from './pages/AdminDashboard';
import { Briefing } from './pages/Briefing';
import { Events } from './pages/Events';
import { Ideas } from './pages/Ideas';
import { IdentityCard } from './pages/IdentityCard';
import { Missions } from './pages/Missions';
import { Onboarding } from './pages/Onboarding';
import { RoomChat } from './pages/RoomChat';
import { Rooms } from './pages/Rooms';
import { apiRequest } from './services/api';
import type { SocketStatus } from './services/socket';

const AppContent: React.FC = () => {
  const { session, loading, isVerifiedMember } = useAuth();
  const [currentTab, setCurrentTab] = useState<NavTab>('briefing');
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [showEvents, setShowEvents] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPushConsentModal, setShowPushConsentModal] = useState(false);
  const [showInstallGuideModal, setShowInstallGuideModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [reportTargetId, setReportTargetId] = useState<string>('general');
  const [socketStatus, setSocketStatus] = useState<SocketStatus>('disconnected');
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState<number>(0);

  // Capture PWA install prompt without aggressive nagging
  useEffect(() => {
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  // Poll unread notification count periodically for ambient status sync
  useEffect(() => {
    if (!session || !isVerifiedMember) return;
    let isMounted = true;

    const fetchUnreadCount = async () => {
      try {
        const res = await apiRequest<{ unreadCount: number }>('/api/notifications?limit=1');
        if (isMounted && typeof res.unreadCount === 'number') {
          setUnreadNotificationsCount(res.unreadCount);
        }
      } catch {
        // Silently continue if background network poll fails
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [session, isVerifiedMember]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090a0f] flex flex-col items-center justify-center space-y-3">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs font-mono text-gray-400">Entering ROOM 404...</span>
      </div>
    );
  }

  // If user is not yet an approved member, guide through Onboarding & Verification pipeline
  if (!session || !isVerifiedMember) {
    return <Onboarding />;
  }

  const handleOpenRoom = (roomId: string) => {
    setActiveRoomId(roomId);
    setShowEvents(false);
    setShowAdmin(false);
  };

  const handleOpenReport = (targetId?: string) => {
    setReportTargetId(targetId || 'general');
    setShowReport(true);
  };

  return (
    <div className="min-h-screen bg-[#090a0f] text-gray-100 flex flex-col">
      {/* Top Header */}
      {!activeRoomId && !showAdmin && !showEvents && (
        <Header
          socketStatus={socketStatus}
          unreadNotificationsCount={unreadNotificationsCount}
          onOpenEvents={() => setShowEvents(true)}
          onOpenNotifications={() => setShowNotifications(true)}
          onOpenReport={() => handleOpenReport('general')}
          onOpenAdmin={() => setShowAdmin(true)}
        />
      )}

      {/* Main Dynamic Viewport */}
      <main className="flex-1 overflow-x-hidden">
        {showAdmin ? (
          <AdminDashboard onBack={() => setShowAdmin(false)} />
        ) : showEvents ? (
          <div className="pt-2">
            <div className="px-4 pb-2">
              <button
                onClick={() => setShowEvents(false)}
                className="text-xs text-gray-400 hover:text-white flex items-center space-x-1"
              >
                <span>← Back</span>
              </button>
            </div>
            <Events />
          </div>
        ) : activeRoomId ? (
          <RoomChat
            roomId={activeRoomId}
            onBack={() => setActiveRoomId(null)}
            onOpenReport={handleOpenReport}
          />
        ) : (
          <>
            {currentTab === 'briefing' && (
              <Briefing
                onNavigateTab={(tab) => setCurrentTab(tab)}
                onOpenRoom={handleOpenRoom}
                onOpenEvents={() => setShowEvents(true)}
                onOpenReport={() => handleOpenReport('general')}
              />
            )}
            {currentTab === 'rooms' && (
              <Rooms onSelectRoom={handleOpenRoom} onOpenReport={handleOpenReport} />
            )}
            {currentTab === 'ideas' && <Ideas />}
            {currentTab === 'missions' && <Missions />}
            {currentTab === 'identity' && <IdentityCard />}
          </>
        )}
      </main>

      {/* Bottom Mobile Navigation (Hidden inside live chatroom) */}
      {!activeRoomId && !showAdmin && !showEvents && (
        <Navigation
          currentTab={currentTab}
          onTabChange={(tab) => {
            setCurrentTab(tab);
            setActiveRoomId(null);
            setShowEvents(false);
            setShowAdmin(false);
          }}
        />
      )}

      {/* Modals */}
      <ReportModal
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        defaultTargetId={reportTargetId}
      />

      <NotificationsModal
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        onUnreadCountChange={(count) => setUnreadNotificationsCount(count)}
        onOpenPushSettings={() => setShowPushConsentModal(true)}
        onOpenInstallGuide={() => setShowInstallGuideModal(true)}
      />

      <PushConsentModal
        isOpen={showPushConsentModal}
        onClose={() => setShowPushConsentModal(false)}
        onOpenInstallGuide={() => {
          setShowPushConsentModal(false);
          setShowInstallGuideModal(true);
        }}
      />

      <InstallGuideModal
        isOpen={showInstallGuideModal}
        onClose={() => setShowInstallGuideModal(false)}
        deferredPrompt={deferredPrompt}
      />
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
